import Fuse from 'fuse.js';

import type { AugmentedArtistCard, WorkType } from '../types/Artists';
import type {
  ArtistCategoryFilter,
  AvailabilityFilter,
  FinishFilter,
  MediumFilter,
  ReleaseFilter,
  ShippingFilter,
} from '../types/Filters';
import { filterProducts } from './product';

export class ProductFilterer {
  private fuse: Fuse<MultiCurrencyProduct>;

  constructor(private products: MultiCurrencyProduct[]) {
    this.fuse = new Fuse(products, {
      keys: ['title', 'artist'],
      shouldSort: false,
      threshold: 0.3,
    });
  }

  filter(filters: {
    availability?: AvailabilityFilter;
    shipping?: ShippingFilter;
    mediums?: MediumFilter[];
    finishes?: FinishFilter[];
    releaseTypes?: ReleaseFilter[];
    searchTerm?: string;
  }) {
    let filtered = this.products;

    // Search filter
    if (filters.searchTerm) {
      const results = this.fuse.search(filters.searchTerm);
      filtered = results.map((result) => result.item);
    }

    return filterProducts(filtered, filters);
  }
}

export class ArtistFilterer {
  private fuse: Fuse<AugmentedArtistCard>;

  constructor(private artists: AugmentedArtistCard[]) {
    this.fuse = new Fuse(artists, {
      keys: ['name'],
      shouldSort: false,
      threshold: 0.3,
    });
  }

  filter(filters: {
    categories: ArtistCategoryFilter[];
    mediums: MediumFilter[];
    searchTerm?: string;
    isEstate?: boolean;
  }) {
    let filtered = this.artists.filter((a) => !!a.name);

    // Search filter
    if (filters.searchTerm) {
      const results = this.fuse.search(filters.searchTerm);
      filtered = results.map((result) => result.item);
    }

    // isEstate filter
    filtered = filtered.filter((artist) => {
      return filters.isEstate ? artist.isEstate === true : !artist.isEstate;
    });

    // Category filter
    if (filters.categories.length > 0) {
      filtered = filtered.filter((artist) => {
        return (
          artist.works !== undefined &&
          artist.works.length > 0 &&
          artist.works.some((w) =>
            filters.categories.some(
              (categoryName) =>
                categoryName === 'All' ||
                w.availability ===
                  AVAILABILITY_MAP[
                    categoryName.toLowerCase() as keyof typeof AVAILABILITY_MAP
                  ],
            ),
          )
        );
      });
    }

    // Medium filter
    if (filters.mediums.length > 0) {
      filtered = filtered.filter((artist) => {
        return (
          artist.works !== undefined &&
          artist.works.length > 0 &&
          artist.works.some((w) =>
            filters.mediums.some(
              (mediumName) =>
                mediumName === 'All' ||
                w.type ===
                  WORK_TYPES_MAP[
                    mediumName.toLowerCase() as keyof typeof WORK_TYPES_MAP
                  ],
            ),
          )
        );
      });
    }

    return filtered;
  }
}

const WORK_TYPES_MAP = {
  'print editions': 'PRINT' as WorkType,
  'sculpture editions': 'SCULPTURE' as WorkType,
  'studio works': 'STUDIO WORKS' as WorkType,
  'collectibles': 'COLLECTIBLE' as WorkType,
  'nfts': 'NFT' as WorkType,
};

const AVAILABILITY_MAP = {
  'available': 'available',
  'upcoming': 'upcoming',
};
